/**
 * Desktop UI scale. Grid text/rows are sized in fixed px, which looks tiny on a
 * large monitor. On a fine-pointer (mouse) device we scale them up with the
 * window width: 1× up to 1280px wide, growing to 1.75× on very large screens.
 * Touch devices (phone/tablet) always stay at 1×.
 *
 * Evaluated once at load (module constants elsewhere depend on it) — a reload
 * picks up a different monitor/window size. Also exposed as the `--ui-scale`
 * CSS variable for stylesheet rules (e.g. the officer panel width, and the
 * `.ui-zoom` class that enlarges the header, tab bar and Settings page).
 */
export function computeUiScale(width: number, finePointer: boolean): number {
  if (!finePointer || width < 1100) return 1;
  const s = Math.round((width / 1280) * 20) / 20;
  return Math.min(1.75, Math.max(1, s));
}

const FINE = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches;

function detect(): number {
  if (typeof window === 'undefined') return 1;
  return computeUiScale(window.innerWidth, FINE);
}

export const UI_SCALE = detect();
/** A mouse/trackpad device (as opposed to a phone or tablet). */
export const IS_FINE_POINTER = FINE;

/** Scale a px value, rounded to a whole pixel. */
export const sc = (px: number): number => Math.round(px * UI_SCALE);

if (typeof document !== 'undefined') {
  document.documentElement.style.setProperty('--ui-scale', String(UI_SCALE));
}

/**
 * Pop-ups (modals, drawers, popovers) are zoomed by UI_SCALE on a desktop, the same factor the grids
 * scale their text by, so their type keeps its place beside the graph's instead of staying phone-sized
 * on a big monitor. The graph's cell text is 12px × UI_SCALE and its smallest density is 70% of that,
 * so no pop-up text may be smaller than POPUP_MIN_FONT_PX before the zoom (see popupFont.test.ts).
 */
export const POPUP_MIN_FONT_PX = Math.ceil(0.7 * 12);

/**
 * A `vh` height for a zoomed pop-up: zoom multiplies vh too, so the number is divided by UI_SCALE to
 * land on the same share of the screen.
 */
export const popupMaxH = (vh: number, unit: 'vh' | 'dvh' = 'vh'): string => `${+(vh / UI_SCALE).toFixed(2)}${unit}`;
